import React from 'react';
import { matchPath, useBlocker } from 'react-router-dom';
import { PATH } from '@/router/PATH';

export type UseLeaveConfirmationResult = {
    /** A navigation out of the flow is waiting for the user's answer. */
    isOpen: boolean;
    /** Cancel: stay where you are. */
    onStay: () => void;
    /** Confirm: run `onLeave` (the store's reset), then let it through. */
    onLeave: () => void;
};

/**
 * Asks before a navigation would lose unsaved import work.
 *
 * Built on react-router's `useBlocker`, the app's first use of it (it needs
 * the data router `frontend.tsx` creates). Only navigations **out of**
 * `scopePath` are blocked, so moving between the flow's own steps, the
 * wizard's Finish and Result's "Import another file" go through. Signing
 * out (`/login`) is never blocked: it is an explicit act, and the session is
 * already gone by the time the navigation happens.
 *
 * **While `isLocked`** (an import is on its way) every other navigation to
 * another path is held where it is, inside the flow too (browser Back
 * included), and no dialog opens: the import's answer decides where the
 * flow goes next, and leaving would not undo it. A change of the address's
 * query alone (a tab of the table) goes through. The lock is read when a
 * navigation starts, from the latest render, so the step's own move to
 * Result right after the answer is not held by a lock just released.
 *
 * A reload or a closed tab cannot be blocked in-app; while `isDirty`, the
 * browser's own `beforeunload` prompt covers them.
 */
export const useLeaveConfirmation = (
    isDirty: boolean,
    scopePath: string,
    onLeave: () => void,
    isLocked = false,
): UseLeaveConfirmationResult => {
    // React Router swaps the blocker function in an effect, which a
    // navigation made in another component's effect can beat; a ref written
    // on render is never behind.
    const isLockedRef = React.useRef(isLocked);
    isLockedRef.current = isLocked;

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (matchPath(PATH.LOGIN, nextLocation.pathname) !== null) {
            return false;
        }
        if (isLockedRef.current) {
            return nextLocation.pathname !== currentLocation.pathname;
        }
        return (
            isDirty &&
            matchPath(
                { path: scopePath, end: false },
                nextLocation.pathname,
            ) === null
        );
    });

    // A navigation held by the lock stays where it is, with no question.
    // Before paint, so the dialog never flashes.
    React.useLayoutEffect(() => {
        if (blocker.state === 'blocked' && isLockedRef.current) {
            blocker.reset();
        }
    }, [blocker]);

    React.useEffect(() => {
        if (!isDirty) {
            return undefined;
        }
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            // Older browsers show the prompt only when this is set.
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    const onStay = React.useCallback(() => {
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
    }, [blocker]);

    const handleLeave = React.useCallback(() => {
        if (blocker.state === 'blocked') {
            onLeave();
            blocker.proceed();
        }
    }, [blocker, onLeave]);

    return {
        isOpen: blocker.state === 'blocked' && !isLocked,
        onStay,
        onLeave: handleLeave,
    };
};
